import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { usePlayer } from '../lib/player'
import { toTitleCase } from '../lib/format'
import { artGradient } from '../lib/songColour'
import { getCached, setCached } from '../lib/queryCache'
import { Shell } from '../components/Shell'
import { Player, Spinner, initials, isVideo } from '../components/ui'
import ReportSong from '../components/ReportSong'

/**
 * The front door. Browsable, not personal — nothing here is a draft and
 * nothing here is yours. That distinction used to live on the same screen as
 * "your songs" (the old /songs list), which meant every visit mixed unfinished
 * private work in with things written to be discovered. They're different
 * jobs: this page is for finding what other people have opened out to you;
 * your own catalog, drafts included, lives on your profile now.
 *
 * Same RLS shape as Collective: query everything, can_reach_song() decides
 * what comes back. Grouped into sections by the reach tier a song arrived
 * through, rather than one flat list behind a filter chip — a church's songs
 * and a stranger's collective song are not the same kind of find.
 */

const SECTION_ORDER = ['church', 'network', 'collective', 'inner']
// Parallel phrasing on purpose. These read one after another down a single
// scroll, and "Songs from your church" followed by "From the collective" makes
// the reader re-parse a heading that carries no new information.
const SECTION_LABEL = {
  church: 'From your church',
  network: 'From your writers circle',
  collective: 'From the collective',
  inner: 'From an inner circle',
}

export default function Home() {
  const { user } = useSession()
  const nav = useNavigate()
  const player = usePlayer()
  const [songs, setSongs] = useState(() => getCached('home:' + user.id))
  const [q, setQ] = useState('')
  // Video only. Audio hands off to the mini player so it survives navigation;
  // a video you have to watch stays on the screen that owns it.
  const [videoOpen, setVideoOpen] = useState(null)
  const [srcMap, setSrcMap] = useState({})
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('songs')
      .select(`id, title, reach, finished_at, r2_object_key, media_mime, media_filename,
                duration_seconds, scripture_refs, allow_covers, open_feedback,
                songwriter_id, users:songwriter_id(first_name, last_name)`)
      .not('finished_at', 'is', null)
      .neq('songwriter_id', user.id)
      .order('finished_at', { ascending: false })
    if (error) return setErr(readableError(error))
    setCached('home:' + user.id, data ?? [])
    setSongs(data ?? [])
  }, [user.id])

  useEffect(() => { load() }, [load])

  const writer = (s) =>
    [s.users?.first_name, s.users?.last_name].filter(Boolean).join(' ') || 'A writer'

  const filtered = useMemo(() => {
    if (!songs) return []
    const term = q.trim().toLowerCase()
    if (!term) return songs
    return songs.filter((s) =>
      (s.title || '').toLowerCase().includes(term) ||
      writer(s).toLowerCase().includes(term) ||
      (s.scripture_refs || []).some((r) => r.toLowerCase().includes(term))
    )
  }, [songs, q])

  // One song to put forward, not a list — the featured slot only means
  // something if it's rare. A collective song actively looking for feedback
  // or covers is the best fit; failing that, the newest collective song.
  const featured = useMemo(() => {
    const collective = filtered.filter((s) => s.reach === 'collective')
    return collective.find((s) => s.open_feedback || s.allow_covers) ?? collective[0] ?? null
  }, [filtered])

  const sections = useMemo(() => {
    const rest = filtered.filter((s) => s.id !== featured?.id)
    return SECTION_ORDER
      .map((tier) => ({ tier, items: rest.filter((s) => s.reach === tier) }))
      .filter((sec) => sec.items.length > 0)
  }, [filtered, featured])

  if (songs === null) return <Shell tabs><Spinner /></Shell>

  /** Resolve the signed URL once per song, then reuse it. */
  const srcFor = async (s) => {
    if (srcMap[s.id]) return srcMap[s.id]
    const { data, error } = await supabase.functions
      .invoke('r2-playback-url', { body: { song_id: s.id } })
    if (error) { setErr(readableError(error)); return null }
    const url = data?.url ?? null
    setSrcMap((m) => ({ ...m, [s.id]: url }))
    return url
  }

  const play = async (s) => {
    // Already the loaded track — let the provider toggle it rather than
    // re-fetching a URL we already have.
    if (player.isCurrent(s.id)) return player.toggle()
    const url = await srcFor(s)
    if (!url) return
    player.play({
      id: s.id, src: url, mime: s.media_mime,
      title: s.title || 'Untitled', writer: writer(s),
      writerId: s.songwriter_id,
      duration: s.duration_seconds,
    })
  }

  const openVideo = async (s) => {
    if (videoOpen === s.id) return setVideoOpen(null)
    if (!(await srcFor(s))) return
    setVideoOpen(s.id)
  }

  /**
   * A row, not a card. The row itself opens the song, so the only button left
   * is the one that does something different — play. That's what removed two
   * same-weight outlined pills from every song on the screen.
   *
   * No reach pip: this screen groups by reach, so a pip repeated its own
   * section header on every row. The Collective tab is a flat list and still
   * needs it; a grouped list does not.
   */
  const songCard = (s) => {
    const video = isVideo(s.media_mime)
    const current = player.isCurrent(s.id)
    return (
      <article key={s.id} className="tile feedsong">
        <div className="songrow">
          {s.r2_object_key ? (
            <button className={`ava is-art is-playable${current && player.playing ? ' on' : ''}`}
                    style={{ background: artGradient(s.id) }}
                    onClick={() => (video ? openVideo(s) : play(s))}
                    aria-label={current && player.playing ? `Pause ${s.title}` : `Play ${s.title}`}>
              {current && player.playing ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="7" y="5" width="3.4" height="14" rx="1.1" />
                  <rect x="13.6" y="5" width="3.4" height="14" rx="1.1" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5.6v12.8c0 .7.8 1.2 1.4.8l9.6-6.4c.5-.4.5-1.2 0-1.6L9.4 4.8c-.6-.4-1.4 0-1.4.8Z" />
                </svg>
              )}
            </button>
          ) : (
            <span className="ava is-art" style={{ background: artGradient(s.id) }}
                  aria-hidden="true">{initials(writer(s))}</span>
          )}

          <button className="songopen" onClick={() => nav(`/songs/${s.id}/learn`)}>
            <span className="songmeta">
              <b>{toTitleCase(s.title) || 'Untitled'}</b>
              <small>{writer(s)}</small>
              {s.scripture_refs?.length > 0 && (
                <span className="ref">{s.scripture_refs[0]}</span>
              )}
            </span>
          </button>

          {!s.r2_object_key && <span className="muted">Words only</span>}

          <ReportSong songId={s.id} title={s.title} />
        </div>

        {video && videoOpen === s.id && (
          <div className="songvideo">
            <Player src={srcMap[s.id]} mime={s.media_mime}
                    duration={s.duration_seconds} filename={s.media_filename} />
          </div>
        )}
      </article>
    )
  }

  return (
    <Shell tabs>
      <div className="eyebrow">Secret Place</div>
      <h2 className="q">Home.</h2>
      <p className="sub">What other writers have opened out to you.</p>

      <div className="field is-search" style={{ marginBottom: 18 }}>
        <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search songs, writers, scripture…" />
      </div>

      {songs.length === 0 ? (
        <div className="empty">
          <p className="sub" style={{ marginBottom: 6 }}>Nothing has reached you yet.</p>
          <p className="small">
            This fills as writers open songs out. Being in someone's circle is the
            quickest way in — and the songs that arrive that way are usually the
            ones still being worked on.
          </p>
        </div>
      ) : (
        <>
          {featured && (
            <button className="tile featured" onClick={() => nav(`/songs/${featured.id}/learn`)}>
              <div className="eyebrow" style={{ marginBottom: 6 }}>Featured from the collective</div>
              <h3 style={{ fontSize: 19, marginBottom: 6 }}>{toTitleCase(featured.title) || 'Untitled'}</h3>
              {/* Said once. This used to carry the invitation in the byline
                  AND repeat it as two chips directly underneath — and those
                  chips are 40px bordered pills, so a card that is itself one
                  big button appeared to hold two more. */}
              <p className="small" style={{ margin: 0 }}>
                {writer(featured)}
                {featured.open_feedback && featured.allow_covers
                  ? ' · Open to feedback and covers'
                  : featured.open_feedback ? ' · Open to feedback'
                  : featured.allow_covers ? ' · Open to covers' : ''}
              </p>
            </button>
          )}

          {sections.map(({ tier, items }) => (
            <div key={tier}>
              <div className="rowhead">
                <div className="eyebrow">{SECTION_LABEL[tier]}</div>
                {items.length > 3 && (
                  <button className="btn-plain" style={{ minHeight: 0, padding: 0 }}
                          onClick={() => nav('/collective')}>See all</button>
                )}
              </div>
              {/* One grouped surface per section, with hairlines between rows —
                  the iOS inset list. A card each made four shadows where the
                  section only needed one. */}
              <div className="songgroup">{items.slice(0, 3).map(songCard)}</div>
            </div>
          ))}

          {sections.length === 0 && !featured && (
            <p className="small">Nothing at that search right now.</p>
          )}
        </>
      )}

      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
